import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Monitor, Code, Wrench, ShoppingBag, Heart, Trees as Tree, Download, GraduationCap, Building2, Users, Clock, CheckCircle2, X, Calendar } from 'lucide-react';

type Course = {
  id: string;
  title: string;
  icon: React.ElementType;
  description: string;
  skills: string[];
  duration: string;
  internship: string;
  careers: string[];
  image: string;
  startDate: string;
  slots: number;
  level: string;
  learningPoints: string[];
  modules: { title: string; description: string }[];
  enrollmentLink: string;
};

const courses: Course[] = [
  {
    id: 'multimedia',
    title: 'Técnico Multimédia',
    icon: Monitor,
    description: 'Forma profissionais capazes de conceber e desenvolver produtos multimédia interativos, integrando competências nos diferentes média através de tecnologias e aplicações digitais.',
    skills: [
      'Design gráfico e web design',
      'Produção de vídeo e áudio',
      'Animação digital',
      'Fotografia',
      'Desenvolvimento web'
    ],
    duration: '3 anos letivos',
    internship: '600 horas em contexto real de trabalho',
    careers: [
      'Designer multimédia',
      'Editor de vídeo',
      'Web designer',
      'Fotógrafo',
      'Motion designer'
    ],
    image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    startDate: '1 de Setembro de 2024',
    slots: 20,
    level: 'Técnico',
    learningPoints: [
      'Conhecimento em design gráfico',
      'Habilidades em animação digital',
      'Comprometimento com a sustentabilidade',
      'Capacidade de trabalhar em equipe',
      'Inovação e criatividade'
    ],
    modules: [
      { title: 'Introdução ao Design Multimédia', description: 'Conceitos básicos de design gráfico e web design' },
      { title: 'Produção de Vídeo e Áudio', description: 'Técnicas de gravação e edição de vídeo e áudio' },
      { title: 'Animação Digital', description: 'Criação de animações utilizando softwares de animação' },
      { title: 'Fotografia Digital', description: 'Técnicas de fotografia digital e edição de imagens' },
      { title: 'Desenvolvimento Web', description: 'Construção de sites e aplicações web' }
    ],
    enrollmentLink: 'https://example.com/multimedia'
  },
  {
    id: 'programming',
    title: 'Técnico Gestão Programação Sistemas Informação',
    icon: Code,
    description: 'Prepara profissionais para desenvolver aplicações informáticas, gerir sistemas de informação e administrar sistemas informáticos.',
    skills: [
      'Programação em várias linguagens',
      'Gestão de bases de dados',
      'Desenvolvimento web',
      'Redes de computadores',
      'Segurança informática'
    ],
    duration: '3 anos letivos',
    internship: '600 horas em contexto real de trabalho',
    careers: [
      'Programador',
      'Administrador de sistemas',
      'Desenvolvedor web',
      'Gestor de projetos IT',
      'Analista de sistemas'
    ],
    image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    startDate: '1 de Setembro de 2024',
    slots: 15,
    level: 'Técnico',
    learningPoints: [
      'Conhecimento em programação',
      'Habilidades em gestão de bases de dados',
      'Comprometimento com a segurança informática',
      'Capacidade de trabalhar em equipe',
      'Inovação e criatividade'
    ],
    modules: [
      { title: 'Introdução à Programação', description: 'Conceitos básicos de programação' },
      { title: 'Gestão de Bases de Dados', description: 'Técnicas de gestão e manipulação de bases de dados' },
      { title: 'Desenvolvimento Web', description: 'Construção de sites e aplicações web' },
      { title: 'Segurança Informática', description: 'Práticas de segurança em sistemas informáticos' },
      { title: 'Redes de Computadores', description: 'Conceitos básicos de redes de computadores' }
    ],
    enrollmentLink: 'https://example.com/programming'
  },
  {
    id: 'industrial',
    title: 'Técnico de Manutenção Industrial',
    icon: Wrench,
    description: 'Forma profissionais para planear, coordenar e executar a manutenção de equipamentos e sistemas industriais.',
    skills: [
      'Mecânica industrial',
      'Eletricidade',
      'Automação',
      'Hidráulica e pneumática',
      'Desenho técnico'
    ],
    duration: '3 anos letivos',
    internship: '600 horas em contexto real de trabalho',
    careers: [
      'Técnico de manutenção',
      'Operador de máquinas CNC',
      'Técnico de automação',
      'Gestor de manutenção',
      'Técnico de qualidade'
    ],
    image: 'https://images.unsplash.com/photo-1581092921461-eab62e97a780?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    startDate: '1 de Setembro de 2024',
    slots: 10,
    level: 'Técnico',
    learningPoints: [
      'Conhecimento em mecânica industrial',
      'Habilidades em eletricidade',
      'Comprometimento com a automação',
      'Capacidade de trabalhar em equipe',
      'Inovação e criatividade'
    ],
    modules: [
      { title: 'Introdução à Manutenção Industrial', description: 'Conceitos básicos de manutenção industrial' },
      { title: 'Eletricidade Industrial', description: 'Técnicas de eletricidade industrial' },
      { title: 'Automação Industrial', description: 'Técnicas de automação industrial' },
      { title: 'Hidráulica e Pneumática Industrial', description: 'Técnicas de hidráulica e pneumática industrial' },
      { title: 'Desenho Técnico', description: 'Técnicas de desenho técnico' }
    ],
    enrollmentLink: 'https://example.com/industrial'
  },
  {
    id: 'commercial',
    title: 'Técnico Comercial',
    icon: ShoppingBag,
    description: 'Prepara profissionais para organizar e gerir atividades comerciais em empresas, desenvolvendo competências na área de vendas e marketing.',
    skills: [
      'Técnicas de vendas',
      'Marketing digital',
      'Gestão comercial',
      'Atendimento ao cliente',
      'Negociação'
    ],
    duration: '3 anos letivos',
    internship: '600 horas em contexto real de trabalho',
    careers: [
      'Gestor comercial',
      'Consultor de vendas',
      'Gestor de loja',
      'Marketing manager',
      'Account manager'
    ],
    image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    startDate: '1 de Setembro de 2024',
    slots: 12,
    level: 'Técnico',
    learningPoints: [
      'Conhecimento em técnicas de vendas',
      'Habilidades em marketing digital',
      'Comprometimento com o atendimento ao cliente',
      'Capacidade de negociação',
      'Inovação e criatividade'
    ],
    modules: [
      { title: 'Introdução ao Comércio', description: 'Conceitos básicos de comércio' },
      { title: 'Marketing Digital', description: 'Técnicas de marketing digital' },
      { title: 'Gestão Comercial', description: 'Técnicas de gestão comercial' },
      { title: 'Atendimento ao Cliente', description: 'Técnicas de atendimento ao cliente' },
      { title: 'Negociação', description: 'Técnicas de negociação' }
    ],
    enrollmentLink: 'https://example.com/commercial'
  },
  {
    id: 'health',
    title: 'Técnico Auxiliar Saúde',
    icon: Heart,
    description: 'Forma profissionais para auxiliar na prestação de cuidados de saúde, sob orientação de profissionais de saúde.',
    skills: [
      'Cuidados básicos de saúde',
      'Primeiros socorros',
      'Higiene e segurança',
      'Comunicação em saúde',
      'Ética profissional'
    ],
    duration: '3 anos letivos',
    internship: '600 horas em contexto real de trabalho',
    careers: [
      'Auxiliar de saúde',
      'Assistente em hospitais',
      'Cuidador de idosos',
      'Auxiliar em clínicas',
      'Assistente em centros de saúde'
    ],
    image: 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    startDate: '1 de Setembro de 2024',
    slots: 8,
    level: 'Técnico',
    learningPoints: [
      'Conhecimento em cuidados básicos de saúde',
      'Habilidades em primeiros socorros',
      'Comprometimento com a higiene e segurança',
      'Capacidade de comunicação em saúde',
      'Ética profissional'
    ],
    modules: [
      { title: 'Introdução à Saúde', description: 'Conceitos básicos de saúde' },
      { title: 'Cuidados Básicos de Saúde', description: 'Técnicas de cuidados básicos de saúde' },
      { title: 'Primeiros Socorros', description: 'Técnicas de primeiros socorros' },
      { title: 'Higiene e Segurança', description: 'Práticas de higiene e segurança' },
      { title: 'Comunicação em Saúde', description: 'Técnicas de comunicação em saúde' }
    ],
    enrollmentLink: 'https://example.com/health'
  },
  {
    id: 'forestry',
    title: 'Técnico de Recursos Florestais e Ambientais',
    icon: Tree,
    description: 'Prepara profissionais para gerir recursos florestais e ambientais, promovendo a sustentabilidade e conservação da natureza.',
    skills: [
      'Gestão florestal',
      'Conservação ambiental',
      'Topografia',
      'Silvicultura',
      'Proteção civil'
    ],
    duration: '3 anos letivos',
    internship: '600 horas em contexto real de trabalho',
    careers: [
      'Técnico florestal',
      'Gestor ambiental',
      'Técnico de conservação',
      'Consultor ambiental',
      'Técnico de proteção civil'
    ],
    image: 'https://images.unsplash.com/photo-1448375240586-882707db888b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    startDate: '1 de Setembro de 2024',
    slots: 5,
    level: 'Técnico',
    learningPoints: [
      'Conhecimento em gestão florestal',
      'Habilidades em conservação ambiental',
      'Comprometimento com a topografia',
      'Capacidade de silvicultura',
      'Inovação e criatividade'
    ],
    modules: [
      { title: 'Introdução aos Recursos Florestais', description: 'Conceitos básicos de recursos florestais' },
      { title: 'Gestão Florestal', description: 'Técnicas de gestão florestal' },
      { title: 'Conservação Ambiental', description: 'Técnicas de conservação ambiental' },
      { title: 'Topografia', description: 'Técnicas de topografia' },
      { title: 'Silvicultura', description: 'Técnicas de silvicultura' }
    ],
    enrollmentLink: 'https://example.com/forestry'
  }
];

const Courses = () => {
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-gradient-to-b from-background to-secondary/20">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative mb-12"
      >
        <div
          className="h-[60vh] relative flex items-center justify-center"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.8)), url("https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80")',
            backgroundAttachment: 'fixed',
            backgroundPosition: 'center',
            backgroundSize: 'cover'
          }}
        >
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Cursos Técnico-Profissionais</h1>
            <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto">
              Descubra os cursos profissionais do AEAAV e prepare-se para uma carreira de sucesso.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Courses Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid gap-8 md:grid-cols-2 lg:grid-cols-3"
        >
          {courses.map((course) => (
            <motion.div
              key={course.id}
              variants={itemVariants}
              className="group bg-card rounded-lg overflow-hidden hover:ring-1 hover:ring-primary/50 transition-all cursor-pointer"
              onClick={() => setSelectedCourse(course)}
            >
              <div
                className="h-48 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                style={{ backgroundImage: `url(${course.image})` }}
              />
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <course.icon className="w-6 h-6 text-primary" />
                  <h2 className="text-xl font-semibold group-hover:text-primary transition-colors">
                    {course.title}
                  </h2>
                </div>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                  {course.description}
                </p>
                <button className="text-primary hover:text-primary/80 text-sm font-medium transition-colors">
                  Saber mais →
                </button>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Course Details Modal */}
      {selectedCourse && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[200]"
          onClick={() => setSelectedCourse(null)}
        >
          <button
            onClick={() => setSelectedCourse(null)}
            className="fixed top-4 right-4 p-2 bg-card/95 hover:bg-card rounded-full transition-all duration-200 hover:scale-110 z-[201]"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="min-h-screen py-8 px-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-card/95 backdrop-blur-sm rounded-lg w-full max-w-4xl mx-auto shadow-2xl border border-primary/20 relative flex flex-col mt-16"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-64">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${selectedCourse.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <selectedCourse.icon className="w-8 h-8 text-primary" />
                    <h2 className="text-2xl font-bold">{selectedCourse.title}</h2>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-400 mb-8">{selectedCourse.description}</p>

                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Detalhes do Curso</h3>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary" />
                        <span>Duração: {selectedCourse.duration}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        <span>Início: {selectedCourse.startDate}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-primary" />
                        <span>Vagas: {selectedCourse.slots}</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <GraduationCap className="w-5 h-5 text-primary" />
                        <span>Nível: {selectedCourse.level}</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-4">O que vai aprender</h3>
                    <ul className="space-y-2">
                      {selectedCourse.learningPoints.map((point, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Programa do Curso</h3>
                  <div className="space-y-4">
                    {selectedCourse.modules.map((module, index) => (
                      <div
                        key={index}
                        className="bg-secondary/50 rounded-lg p-4 hover:bg-secondary/70 transition-colors"
                      >
                        <h4 className="font-medium mb-2">{module.title}</h4>
                        <p className="text-sm text-gray-400">{module.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 flex justify-end gap-4 p-6 bg-card/95 backdrop-blur-sm border-t border-primary/10">
                <button
                  onClick={() => setSelectedCourse(null)}
                  className="px-6 py-3 bg-secondary/80 text-secondary-foreground rounded-lg hover:bg-secondary/60 transition-all duration-200 hover:scale-105 border border-primary/10"
                >
                  Fechar
                </button>
                <button
                  onClick={() => window.location.href = selectedCourse.enrollmentLink}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all duration-200 hover:scale-105 shadow-lg shadow-primary/20"
                >
                  Inscrever-se
                </button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Courses;